// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * Browser-side event bridge between the global Companion launcher and
 * a page that wants to react to a `fund:<key>` click in an assistant
 * reply. Today only `FundsPage` listens — when the user clicks a fund
 * link from any other route, the event has no listener and the click
 * is a no-op (we deliberately don't auto-navigate to /funds; the
 * Phase 5 polish ticket will add a smart-navigate behaviour).
 *
 * Using a window CustomEvent keeps the launcher in the App shell
 * free of routing knowledge — `FundsPage` declares its own listener
 * in a `useEffect`.
 */
export const COMPANION_FUND_SELECT_EVENT = 'companion:fund-select';

export interface CompanionFundSelectDetail {
  fundKey: string;
}

export function dispatchCompanionFundSelect(fundKey: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent<CompanionFundSelectDetail>(COMPANION_FUND_SELECT_EVENT, {
      detail: { fundKey },
    }),
  );
}

export function onCompanionFundSelect(
  handler: (fundKey: string) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (event: Event): void => {
    const detail = (event as CustomEvent<CompanionFundSelectDetail>).detail;
    if (detail?.fundKey) handler(detail.fundKey);
  };
  window.addEventListener(COMPANION_FUND_SELECT_EVENT, listener);
  return () => window.removeEventListener(COMPANION_FUND_SELECT_EVENT, listener);
}
